/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/shared/unitSelectOptions.ts.
 */

import {
  formatUnitOptionLabel,
  formatUnitSymbol,
} from "../../../../../utils/formatUnitSymbol";
import { normalizeUnitToken } from "./fuelMixReferenceAutofill";

/** Type definition — Contributor Unit Option. */
export type ContributorUnitOption = {
  id: number;
  name?: string;
  symbol?: string;
  unitSymbol?: string;
  displayName?: string;
};

/** Type definition — Unit Select Option Row. */
export type UnitSelectOptionRow = {
  value: string;
  label: string;
};

function unitToSelectOption(unit: ContributorUnitOption): UnitSelectOptionRow {
  return {
    value: String(unit.id),
    label: formatUnitOptionLabel(unit),
  };
}

function findUnitById(
  unitId: string,
  ...lists: ContributorUnitOption[][]
): ContributorUnitOption | undefined {
  for (const list of lists) {
    const hit = list.find((u) => String(u.id) === unitId);
    if (hit) return hit;
  }
  return undefined;
}

/** Resolve kg CO₂e unit id from the global units index (best-effort). */
export function resolveKgCo2eUnitId(
  unitIdByToken: Record<string, number>
): string {
  const tokens = [
    "kg co2e",
    "kgco2e",
    "kg co2 eq",
    "kgco2eq",
    normalizeUnitToken("kg CO2e"),
    normalizeUnitToken("kg CO₂e"),
    normalizeUnitToken("kg CO2eq"),
  ];
  for (const token of tokens) {
    const id = unitIdByToken[token];
    if (id != null && Number.isFinite(Number(id))) {
      return String(id);
    }
  }
  for (const [key, id] of Object.entries(unitIdByToken)) {
    if (
      key.includes("kg") &&
      key.includes("co2") &&
      !key.includes("/") &&
      Number.isFinite(Number(id))
    ) {
      return String(id);
    }
  }
  return "";
}

/**
 * Map a unit label/token to a `<select>` value (numeric unit id when available).
 */
export function resolveUnitSelectValueFromLabel(
  label: string,
  unitIdByToken: Record<string, number>,
  dropdownOptions: { value?: string | number; label?: string }[] = []
): string {
  const token = String(label ?? "").trim();
  if (!token) return "";

  const target = normalizeUnitToken(token);
  if (dropdownOptions.length > 0) {
    const hit = dropdownOptions.find((o) => {
      const lbl = normalizeUnitToken(o.label);
      const val = normalizeUnitToken(String(o.value ?? ""));
      return (
        lbl === target ||
        val === target ||
        (target && lbl && (lbl.includes(target) || target.includes(lbl)))
      );
    });
    if (hit?.value !== undefined && hit?.value !== null) {
      return String(hit.value);
    }
  }

  const lower = token.toLowerCase();
  const direct = unitIdByToken[lower] ?? unitIdByToken[target];
  if (direct != null && Number.isFinite(Number(direct))) {
    return String(direct);
  }

  for (const [key, id] of Object.entries(unitIdByToken)) {
    if (!Number.isFinite(Number(id))) continue;
    if (key === target || key === lower) return String(id);
    if (target && (key.includes(target) || target.includes(key))) {
      return String(id);
    }
  }

  return "";
}

/** Exported function — build Contributor Unit Select Options. */
export function buildContributorUnitSelectOptions(params: {
  propertyUnits: ContributorUnitOption[];
  allUnitsFlat?: ContributorUnitOption[];
  dropdownOptions?: { value?: string | number; label?: string }[];
  selected?: string;
}): { options: UnitSelectOptionRow[]; selectValue: string } {
  const {
    propertyUnits,
    allUnitsFlat = [],
    dropdownOptions = [],
    selected = "",
  } = params;

  const trimmed = String(selected ?? "").trim();
  let options: UnitSelectOptionRow[] = [];

  if (propertyUnits.length > 0) {
    options = propertyUnits.map(unitToSelectOption);
  } else if (dropdownOptions.length > 0) {
    options = dropdownOptions.map((opt) => ({
      value: String(opt.value ?? ""),
      label: String(opt.label ?? opt.value ?? ""),
    }));
  }

  if (!trimmed) {
    return { options, selectValue: "" };
  }

  if (options.some((opt) => opt.value === trimmed)) {
    return { options, selectValue: trimmed };
  }

  if (!/^\d+$/.test(trimmed)) {
    const normSelected = normalizeUnitToken(trimmed);
    if (normSelected) {
      for (const list of [propertyUnits, allUnitsFlat]) {
        for (const unit of list) {
          const candidates = [
            unit.symbol,
            unit.unitSymbol,
            unit.name,
            unit.displayName,
          ]
            .map((c) => normalizeUnitToken(c))
            .filter(Boolean);
          const matches = candidates.some(
            (c) =>
              c === normSelected ||
              c.includes(normSelected) ||
              normSelected.includes(c)
          );
          if (!matches) continue;
          const resolvedOption = unitToSelectOption(unit);
          if (!options.some((opt) => opt.value === resolvedOption.value)) {
            options = [resolvedOption, ...options];
          }
          return { options, selectValue: resolvedOption.value };
        }
      }
    }
    return { options, selectValue: "" };
  }

  const resolved = findUnitById(trimmed, propertyUnits, allUnitsFlat);
  if (resolved) {
    const resolvedOption = unitToSelectOption(resolved);
    if (!options.some((opt) => opt.value === resolvedOption.value)) {
      options = [resolvedOption, ...options];
    }
    return { options, selectValue: resolvedOption.value };
  }

  return { options, selectValue: "" };
}

/** Human-readable unit label for read-only dataset / submission views. */
export function resolveContributorUnitDisplayLabel(params: {
  selected: string;
  propertyUnits?: ContributorUnitOption[];
  allUnitsFlat?: ContributorUnitOption[];
  dropdownOptions?: { value?: string | number; label?: string }[];
  referenceUnits?: Record<string, string>;
}): string {
  const trimmed = String(params.selected ?? "").trim();
  if (!trimmed) return "";

  const fromReference = params.referenceUnits?.[trimmed];
  if (fromReference) return formatUnitSymbol(fromReference);

  const { options } = buildContributorUnitSelectOptions({
    propertyUnits: params.propertyUnits ?? [],
    allUnitsFlat: params.allUnitsFlat ?? [],
    dropdownOptions: params.dropdownOptions ?? [],
    selected: trimmed,
  });
  const match = options.find((opt) => opt.value === trimmed);
  if (match?.label) return formatUnitSymbol(match.label);

  return /^\d+$/.test(trimmed) ? "" : formatUnitSymbol(trimmed);
}

/** Exported function — resolve Contributor Dropdown Display Label. */
export function resolveContributorDropdownDisplayLabel(params: {
  selected: string;
  options?: Array<{ value?: string | number; id?: number; label?: string }>;
  referenceMap?: Record<string, string>;
}): string {
  const trimmed = String(params.selected ?? "").trim();
  if (!trimmed) return "";

  const fromReference = params.referenceMap?.[trimmed];
  if (fromReference) return fromReference;

  const match = (params.options ?? []).find(
    (opt) => String(opt.value ?? opt.id ?? "") === trimmed
  );
  if (match?.label) return String(match.label);

  return /^\d+$/.test(trimmed) ? "" : trimmed;
}
